import { NetworkStatus } from '@apollo/client';
import {
  CacheKeys,
  ErrorBoundary,
  ErrorRetry,
  useConfig,
  useName,
  useSessionStorage,
} from '@bechtel/client-common';
import { useCrewMember, useWeldProcedures } from '@bechtel/client-weld-common';
import {
  drawerValues as defaultValues,
  searchByList,
  WeldDrawerValuesProps,
  WeldSearchDrawer,
} from '@bechtel/client-welds';
import {
  AvatarSize,
  Box,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  MultiSelectCheckbox,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  theme,
  useModalState,
} from '@bechtel/gds';
import {
  CommodityComplexFilterInput,
  CommodityFilterByTerm,
  CommodityStatusCode,
  CrewMember,
  CrewMemberList,
  FilterOperator,
  PersonaType,
  WeldProcedure,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { filter, forEach, get, isEqual, map, some, uniq } from 'lodash';
import { FC, Fragment, Suspense, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useAssociatedCrewMember } from '~/hooks/use-associated-crew-member';
import { useDrawingListQuery } from '~/hooks/use-drawing-list';
import {
  AvatarRegularStyled,
  DrawerContainerStyled,
  HeadingStyled,
  MapMarkerButtonStyled,
  SearchFieldComponentStyled,
  SearchFieldStyled,
  SearchFilterButtonStyled,
  StyledIcon,
} from './styled';
import { WeldFilterInput, WeldFilterProps } from './types';

export const SelectedForeman = ({ selectedItemCount, totalItemCount }: any) => {
  return (
    <Box pl={{ sm: '500' }}>
      <Text size="300" family="graphik" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
        {isEqual(selectedItemCount, totalItemCount)
          ? `${t`All Foremen `}`
          : selectedItemCount == 1
            ? `${t`${selectedItemCount} Foreman`}`
            : selectedItemCount > 1
              ? `${t`${selectedItemCount} Foremen`}`
              : `${t`Foreman`}`}
      </Text>
    </Box>
  );
};

export const SelectedFWE = ({ selectedItemCount, totalItemCount }: any) => {
  return (
    <Box pl={{ sm: '500' }}>
      <Text size="300" family="graphik" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
        {isEqual(selectedItemCount, totalItemCount)
          ? `${t`All FWE `}`
          : selectedItemCount > 0
            ? `${t`${selectedItemCount} FWE`}`
            : `${t`FWE`}`}
      </Text>
    </Box>
  );
};

export const SelectedWPS = ({ selectedItemCount, totalItemCount }: any) => {
  return (
    <Box pl={{ sm: '500' }}>
      <Text size="300" family="graphik" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
        {isEqual(selectedItemCount, totalItemCount)
          ? `${t`All WPS `}`
          : selectedItemCount > 0
            ? `${t`${selectedItemCount} WPS`}`
            : `${t`WPS`}`}
      </Text>
    </Box>
  );
};

export const SelectedDrawing = ({ selectedItemCount, totalItemCount }: any) => {
  return (
    <Box pl={{ sm: '500' }}>
      <Text size="300" family="graphik" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
        {isEqual(selectedItemCount, totalItemCount)
          ? `${t`All Drawing `}`
          : selectedItemCount == 1
            ? `${t`${selectedItemCount} Drawing`}`
            : selectedItemCount > 1
              ? `${t`${selectedItemCount} Drawings`}`
              : `${t`Drawing`}`}
      </Text>
    </Box>
  );
};

const PersonaItem = ({ item }: any) => {
  const { getFullName } = useName();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box css={{ width: '100%' }} justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <HeadingStyled weight="600" css={{ paddingBottom: `${theme.spacings[300]}` }}>
            {getFullName(item.firstName, item.lastName)}
          </HeadingStyled>
        </Box>
      </Box>
    </Box>
  );
};

const WPSItem = ({ item }: any) => {
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Text size="400" family="graphik" color="grey.1000">
        {item?.procedure}
      </Text>
    </Box>
  );
};

const DrawingItem = ({ item }: any) => {
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Text family="graphik" size="400" color="grey.1000">
        {item}
      </Text>
    </Box>
  );
};

export const SearchBar: FC<WeldFilterProps> = ({
  onSearch,
  weldFilter,
  weldTypeId,
  personaType,
  crewMember,
  showMap,
  toggleMap,
  enableProjectMap,
}) => {
  const { i18n } = useLingui();
  const { close, toggle, visible } = useModalState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFWEText, setSearchFWEText] = useState('');
  const [searchWPSText, setSearchWPSText] = useState('');
  const [searchForemanText, setSearchForemanText] = useState('');
  const [searchDrawingText, setSearchDrawingText] = useState('');
  const [selectedWPS, setSelectedWPS] = useState<WeldProcedure[]>([]);
  const [selectedDrawing, setSelectedDrawing] = useState<[]>([]);
  const [selectedForeman, setSelectedForeman] = useState<CrewMember[]>([]);
  const [selectedFWE, setSelectedFWE] = useState<CrewMember[]>([]);
  const [isSelectionChanged, setIsSelectionChanged] = useState<boolean>(false);
  const [filteredFWEListData, setFilteredFWEListData] = useState<CrewMember[]>([]);
  const [filteredForemanListData, setFilteredForemanListData] = useState<CrewMember[]>([]);
  const [weldProceduresListData, setWeldProceduresListData] = useState<WeldProcedure[]>([]);
  const debouncedSearchQuery = useDeferredValue(searchQuery);
  const persistedDrawerValues = useRef(defaultValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<WeldDrawerValuesProps>(
    CacheKeys.WeldDashboardDrawerValues,
    persistedDrawerValues.current || defaultValues
  );
  const debouncedSearchFWEText = useDeferredValue(searchFWEText);
  const debouncedSearchWPSText = useDeferredValue(searchWPSText);
  const debouncedSearchForemanText = useDeferredValue(searchForemanText);
  const debouncedSearchDrawingText = useDeferredValue(searchDrawingText);
  const { features } = useConfig();

  const onDrawerSubmit = useCallback(
    (values: WeldDrawerValuesProps) => {
      setDrawerValues(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setDrawerValues]
  );

  const [
    getWeldProceduresById,
    { error: wpsError, data: wpsData, loading: wpsLoading, networkStatus: wpsNetworkStatus, refetch: wpsRefetch },
  ] = useWeldProcedures();
  useEffect(() => {
    if (personaType == PersonaType.FIELD_WELD_ENGINEER) return;
    getWeldProceduresById({
      variables: {
        paging: {
          cursor: '1',
          limit: 100,
        },
      },
    });
  }, [getWeldProceduresById]);

  const weldProceduresList: WeldProcedure[] = get(wpsData, 'loadWeldProcedures.items', []);

  useEffect(() => {
    if (personaType == PersonaType.FIELD_WELD_ENGINEER) return;
    if (debouncedSearchWPSText.length >= 0 && debouncedSearchWPSText.length < 3) {
      setWeldProceduresListData(weldProceduresList);
    } else {
      const filteredWPSLists: WeldProcedure[] = filter(weldProceduresList, (wps) =>
        wps.procedure.toLowerCase().includes(debouncedSearchWPSText.toLowerCase())
      );
      setWeldProceduresListData(filteredWPSLists);
    }
  }, [debouncedSearchWPSText, weldProceduresList]);
  const [
    getForemen,
    {
      error: foremanError,
      data: foremanData,
      loading: foremanLoading,
      networkStatus: foremanNetworkStatus,
      refetch: foremanRefetch,
    },
  ] = useCrewMember(true);

  const foremanList = useMemo(() => {
    return get(foremanData, 'crewMembers.items', []) as CrewMemberList['items'];
  }, [foremanData]);

  const [
    getFWEList,
    { error: fweError, data: fweData, loading: fweLoading, networkStatus: fweNetworkStatus, refetch: fweRefetch },
  ] = useCrewMember(true);
  useEffect(() => {
    if (personaType !== PersonaType.FIELD_WELD_ENGINEER) return;
    getFWEList({
      variables: {
        input: {
          persona: PersonaType.FIELD_WELD_ENGINEER,
        },
        paging: {
          cursor: '1',
          limit: 50,
        },
      },
    });
  }, [getFWEList, personaType]);

  const fweList = get(fweData, 'crewMembers.items', []) as CrewMemberList['items'];
  const loggedUserPersona = crewMember?.persona;
  const loggedUserId = crewMember?.id;
  const filteredFWEList: CrewMember[] = filter(fweList, (fwe) => fwe.id !== loggedUserId);

  useEffect(() => {
    if (personaType !== PersonaType.FIELD_WELD_ENGINEER) return;
    if (debouncedSearchFWEText.length >= 0 && debouncedSearchFWEText.length < 3) {
      setFilteredFWEListData(filteredFWEList);
    } else {
      const filteredFWELists: CrewMember[] = filter(filteredFWEList, (fwe) =>
        (fwe.firstName.toLowerCase() + fwe.lastName.toLowerCase()).includes(debouncedSearchFWEText.toLowerCase())
      );
      setFilteredFWEListData(filteredFWELists);
    }
  }, [debouncedSearchFWEText, fweList, personaType]);
  const [
    getDrawingList,
    {
      error: drawingError,
      data: drawingData,
      loading: drawingLoading,
      networkStatus: drawingNetworkStatus,
      refetch: drawingRefetch,
    },
  ] = useDrawingListQuery();
  const drawingListData = get(drawingData, 'commodities.items', []);
  const drawingList = uniq(drawingListData.map((item) => item?.weld?.isoDocument?.documentNumber.slice(13)));

  const filteredDrawingList =
    selectedDrawing?.length > 0 && debouncedSearchDrawingText.length === 0
      ? [...selectedDrawing, ...drawingList?.filter((item) => !selectedDrawing?.includes(item))]
      : drawingList;

  const getDrawingFilter = () => {
    const complexFilterInput: CommodityComplexFilterInput = {
      and: [
        {
          term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
          value: weldTypeId,
          operator: FilterOperator.EQUALS,
        },
      ],
      or: [],
    };
    if (personaType === PersonaType.FIELD_WELD_ENGINEER) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });

      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
        values: weldFilter?.searchByFWE?.length > 0 ? [crewMember?.id, ...weldFilter.searchByFWE] : [crewMember?.id],
        operator: FilterOperator.IN,
      });
    } else {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
        value: crewMember?.id,
        operator: FilterOperator.EQUALS,
      });
    }
    if (crewMember?.persona && crewMember.persona === PersonaType.FIELD_WELD_ENGINEER) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PERSONA_TYPE_ID,
        value: crewMember?.persona,
        operator: FilterOperator.EQUALS,
      });
    }

    if (weldFilter?.searchByWPS?.length > 0) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.WELD_PROCEDURE_ID,
        values: weldFilter?.searchByWPS,
        operator: FilterOperator.IN,
      });
    }

    if (weldFilter?.searchByForeman?.length > 0) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.CREW_MEMBER_ID,
        values: [...weldFilter.searchByForeman],
        operator: FilterOperator.IN,
      });
    }

    if (debouncedSearchDrawingText.length > 0) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.DRAWING_NUMBER,
        value: debouncedSearchDrawingText,
        operator: FilterOperator.CONTAINS,
      });
    }

    if (weldFilter?.drawerValues?.weldTypes?.length > 0)
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.CONNECTION_TYPE,
        values: weldFilter?.drawerValues?.weldTypes,
        operator: FilterOperator.IN,
      });
    if (weldFilter?.drawerValues?.weldSizeList?.length === 1) {
      const isSmallBore = some(weldFilter?.drawerValues?.weldSizeList, (item) => {
        return item === CommodityFilterByTerm.SMALL_BORE;
      });
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.NOMINAL_DIAMETER,
        value: '2.5',
        operator: isSmallBore ? FilterOperator.LESS_THAN : FilterOperator.GREATER_THAN,
      });
    }
    if (weldFilter?.drawerValues?.isPriority) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PRIORITY_WELDS,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    }
    if (weldFilter?.searchKeyword.length > 0)
      forEach(weldFilter?.drawerValues?.searchByList, (item: CommodityFilterByTerm) => {
        complexFilterInput.or?.push({
          term: item,
          value: weldFilter?.searchKeyword,
          operator: item == CommodityFilterByTerm.CREW_MEMBER_NAME ? FilterOperator.FUZZY : FilterOperator.CONTAINS,
        });
      });

    return complexFilterInput;
  };

  useEffect(() => {
    if (
      crewMember?.persona === PersonaType.SUPER_ADMIN ||
      (crewMember?.persona === PersonaType.FIELD_WELD_ENGINEER && !weldFilter?.searchByForeman?.length)
    )
      return;
    if (!weldTypeId) return;
    if (weldFilter?.searchKeyword?.length > 0 && weldFilter?.searchKeyword?.length < 3) return;

    const commodityComplexFilterInput = getDrawingFilter();

    commodityComplexFilterInput.and?.push({
      term: CommodityFilterByTerm.INCLUDE_BLOCKED_COMMODITY,
      value: 'true',
      operator: FilterOperator.EQUALS,
    });

    if (!(personaType === PersonaType.FIELD_WELD_ENGINEER))
      commodityComplexFilterInput.and?.push({
        term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    if (personaType === PersonaType.FIELD_WELD_ENGINEER) {
      commodityComplexFilterInput.and?.push({
        term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
        values: [
          CommodityStatusCode.FITUP_INSPECTION,
          CommodityStatusCode.CLAIMED,
          CommodityStatusCode.READY_TO_WELD,
          CommodityStatusCode.VT_SENT_BACK,
        ],
        operator: FilterOperator.IN,
      });
    } else {
      commodityComplexFilterInput.and?.push({
        term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
        values: [CommodityStatusCode.READY_TO_WELD, CommodityStatusCode.FITUP_INSPECTION, CommodityStatusCode.CLAIMED],
        operator: FilterOperator.IN,
      });
    }

    getDrawingList({
      variables: {
        input: {
          complexFilter: commodityComplexFilterInput,
        },
        paging: {
          cursor: '1',
          limit: 50,
        },
      },
    });
  }, [weldFilter, weldTypeId, crewMember, getDrawingList, personaType, debouncedSearchDrawingText]);

  //TODO : need to implement dispatch event of context
  //getSupportingForeman will only call once when page load it response is used to check is supporting forman exist or not
  const [getSupportingForeman, { data: supportingForeman }] = useAssociatedCrewMember(true);
  const supportingForemanList: CrewMember[] = useMemo(() => {
    return get(supportingForeman, 'loadAssociatedCrewMembers.associatedCrewMembers', []);
  }, [supportingForeman]);

  useEffect(() => {
    if (personaType !== PersonaType.FIELD_WELD_ENGINEER) return;
    getSupportingForeman({
      variables: {
        input: {
          crewMemberId: loggedUserId,
        },
      },
    });
  }, []);
  //getAssociatedCrewMember will  only call durning search its response will render  when we search any thing if supporting foreman exist
  const [
    getAssociatedCrewMember,
    {
      data: associatedCrewMemberData,
      error: associatedCrewMemberError,
      loading: associatedCrewMemberLoading,
      refetch: associatedCrewMemberRefetch,
      networkStatus: assocaitedCrewMemberNetworkStatus,
    },
  ] = useAssociatedCrewMember(true);

  useEffect(() => {
    if (personaType !== PersonaType.FIELD_WELD_ENGINEER || supportingForemanList?.length == 0) return;
    getAssociatedCrewMember({
      variables: {
        input: {
          crewMemberId: loggedUserId,
        },
      },
    });
  }, [loggedUserId]);

  const associatedCrewMembers: CrewMember[] = useMemo(() => {
    return get(associatedCrewMemberData, 'loadAssociatedCrewMembers.associatedCrewMembers', []);
  }, [associatedCrewMemberData]);
  const dashboardForemanError = associatedCrewMemberError || foremanError;
  const dashboardForemanLoading = associatedCrewMemberLoading || foremanLoading;
  const dashboardForemanRefetch = associatedCrewMemberRefetch || foremanRefetch;
  const dashboardForemanlist = useMemo(() => {
    return supportingForemanList?.length > 0
      ? associatedCrewMembers?.length == 0 && debouncedSearchForemanText?.length == 0
        ? supportingForemanList
        : associatedCrewMembers
      : foremanList;
  }, [foremanList, supportingForemanList, associatedCrewMembers]);

  const dashboardForemanNetworkStatus = assocaitedCrewMemberNetworkStatus || foremanNetworkStatus;

  useEffect(() => {
    const dashboardForemanIds = map(dashboardForemanlist, (foreman) => foreman?.id);
    const selectedForemanIds = map(selectedForeman, (foreman) => foreman?.id);
    const foremanIds =
      personaType == PersonaType.FIELD_WELD_ENGINEER
        ? isSelectionChanged
          ? selectedForemanIds
          : dashboardForemanIds
        : selectedForemanIds;
    if (debouncedSearchQuery?.length > 0 && debouncedSearchQuery?.length < 3) return;
    if (foremanIds.length == 0 && personaType == PersonaType.FIELD_WELD_ENGINEER) return;
    const weldFilterInput: WeldFilterInput = {
      searchKeyword: debouncedSearchQuery,
      searchByDrawing: map(selectedDrawing, (drawingDocumentNumber) => drawingDocumentNumber),
      searchByWPS: map(selectedWPS, (wps) => wps?.procedure),
      searchByForeman: foremanIds,
      searchByFWE: map(selectedFWE, (fwe) => fwe?.id),
      drawerValues: drawerValues,
    };
    onSearch(weldFilterInput);
  }, [
    selectedDrawing,
    selectedWPS,
    selectedForeman,
    selectedFWE,
    drawerValues,
    debouncedSearchQuery,
    dashboardForemanlist,
  ]);

  useEffect(() => {
    if (
      personaType == PersonaType.FOREMAN ||
      (personaType == PersonaType.FIELD_WELD_ENGINEER && supportingForemanList?.length > 0)
    )
      return;

    getForemen({
      variables: {
        input: {
          persona: PersonaType.FOREMAN,
          supervisorId:
            loggedUserPersona == PersonaType.SUPERINTENDENT || loggedUserPersona == PersonaType.GENERAL_FOREMAN
              ? loggedUserId
              : null,
        },
        paging: {
          cursor: '1',
          limit: personaType == PersonaType.FIELD_WELD_ENGINEER ? 100 : 50,
        },
      },
    });
  }, [getForemen, associatedCrewMembers, loggedUserId, loggedUserPersona]);

  useEffect(() => {
    if (debouncedSearchForemanText.length >= 0 && debouncedSearchForemanText.length < 3) {
      setFilteredForemanListData(dashboardForemanlist);
    } else {
      const filteredForemanLists: CrewMember[] = filter(dashboardForemanlist, (fw) =>
        (fw.firstName.toLowerCase() + fw.lastName.toLowerCase()).includes(debouncedSearchForemanText.toLowerCase())
      );
      setFilteredForemanListData(filteredForemanLists);
    }
  }, [debouncedSearchForemanText, dashboardForemanlist]);

  return (
    <Fragment>
      <Box
        my={{ sm: '400' }}
        mx={{ sm: '450' }}
        justifyContent={{ sm: 'space-between' }}
        data-testid="dashboard-searchbar-container"
      >
        <SearchFieldStyled data-testid="search-filed-container">
          <SearchFieldComponentStyled
            size={TextFieldSize.SMALL}
            iconSize={IconSize.SMALL}
            placeholder={
              drawerValues?.searchByList?.length > 1
                ? t`Search Multiple (${drawerValues?.searchByList?.length})`
                : drawerValues?.searchByList?.length == 1
                  ? t`Search ${i18n._(
                      searchByList.find((searchItem) => searchItem?.value === drawerValues?.searchByList[0])?.label
                    )}`
                  : ''
            }
            type="search"
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            value={searchQuery}
          />
        </SearchFieldStyled>
        <Box
          flexGrow={{ sm: 1 }}
          data-testid="searchbar-drawing-container"
          mx={{ sm: '300' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          style={{ minWidth: '14%' }}
        >
          {!drawingLoading && drawingError && (
            <div data-testid="welder-error">
              <ErrorRetry refetch={drawingRefetch}>
                <Trans>There was a problem loading drawing list</Trans>
              </ErrorRetry>
            </div>
          )}
          {!drawingError && (
            <MultiSelectCheckbox
              loading={drawingLoading || drawingNetworkStatus === NetworkStatus.refetch}
              searchPlaceholder={t`Search Drawing`}
              showSearchBar={true}
              autoPosition={false}
              onSearchInputChange={(text) => {
                if (text.length > 0 && text.length < 3) return;
                setSearchDrawingText(text);
              }}
              term={searchDrawingText}
              setTerm={setSearchDrawingText}
              items={filteredDrawingList}
              itemComponent={DrawingItem}
              selectedComponent={SelectedDrawing}
              size={TextFieldSize.SMALL}
              showSelectAll={true}
              confirmSelection={true}
              appearance={TextFieldAppearance.NORMAL}
              displayKeyField="name"
              placeholder={t`Drawing`}
              onSelectedItemsChange={(selectedItems) => {
                if (!searchDrawingText && selectedItems.length === drawingList.length) {
                  setSelectedDrawing([]);
                } else {
                  setSelectedDrawing(selectedItems);
                }
              }}
            />
          )}
        </Box>
        {personaType != PersonaType.FIELD_WELD_ENGINEER && (
          <Box
            flexGrow={{ sm: 1 }}
            data-testid="searchbar-wps-container"
            mx={{ sm: '300' }}
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            style={{ minWidth: '10%' }}
          >
            {!wpsLoading && wpsError && (
              <div data-testid="welder-error">
                <ErrorRetry refetch={wpsRefetch}>
                  <Trans>There was a problem loading Weld Procedures</Trans>
                </ErrorRetry>
              </div>
            )}
            {!wpsError && (
              <MultiSelectCheckbox
                loading={wpsLoading || wpsNetworkStatus === NetworkStatus.refetch}
                searchPlaceholder={t`Search WPS`}
                showSearchBar={true}
                autoPosition={false}
                onSearchInputChange={(text) => {
                  if (text.length > 0 && text.length < 3) return;
                  setSearchWPSText(text);
                }}
                term={searchWPSText}
                setTerm={setSearchWPSText}
                items={weldProceduresListData}
                itemComponent={WPSItem}
                selectedComponent={SelectedWPS}
                size={TextFieldSize.SMALL}
                showSelectAll={true}
                confirmSelection={true}
                appearance={TextFieldAppearance.NORMAL}
                displayKeyField="procedure"
                placeholder={t`All WPS`}
                onSelectedItemsChange={(selectedItems) => {
                  setSelectedWPS(selectedItems);
                }}
              />
            )}
          </Box>
        )}
        {personaType === PersonaType.FIELD_WELD_ENGINEER && (
          <Box
            flexGrow={{ sm: 1 }}
            data-testid="searchbar-fwe-container"
            mx={{ sm: '400' }}
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            style={{ minWidth: '19%' }}
          >
            {!fweLoading && fweError && (
              <div data-testid="welder-error">
                <ErrorRetry refetch={fweRefetch}>
                  <Trans>There was a problem loading Field Weld Engineers</Trans>
                </ErrorRetry>
              </div>
            )}
            {!fweError && (
              <MultiSelectCheckbox
                loading={fweLoading || fweNetworkStatus === NetworkStatus.refetch}
                searchPlaceholder={t`Search FWE`}
                showSearchBar={true}
                autoPosition={false}
                onSearchInputChange={(text) => {
                  if (text.length > 0 && text.length < 3) return;
                  setSearchFWEText(text);
                }}
                term={searchFWEText}
                setTerm={setSearchFWEText}
                items={filteredFWEListData}
                itemComponent={PersonaItem}
                selectedComponent={SelectedFWE}
                size={TextFieldSize.SMALL}
                showSelectAll={true}
                confirmSelection={true}
                appearance={TextFieldAppearance.NORMAL}
                displayKeyField="name"
                placeholder={t`FWE`}
                onSelectedItemsChange={(selectedItems) => {
                  setSelectedFWE(selectedItems);
                }}
              />
            )}
          </Box>
        )}
        {personaType != PersonaType.FOREMAN && (
          <Box
            flexGrow={{ sm: 1 }}
            data-testid="searchbar-foreman-container"
            mx={{ sm: '400' }}
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            style={{ minWidth: '18%' }}
          >
            {!dashboardForemanLoading && dashboardForemanError && (
              <div data-testid="welder-error">
                <ErrorRetry refetch={dashboardForemanRefetch}>
                  <Trans>There was a problem loading Foremen</Trans>
                </ErrorRetry>
              </div>
            )}
            {!dashboardForemanError && (
              <MultiSelectCheckbox
                loading={dashboardForemanLoading || dashboardForemanNetworkStatus === NetworkStatus.refetch}
                initialSelectedItems={personaType == PersonaType.FIELD_WELD_ENGINEER ? dashboardForemanlist : []}
                searchPlaceholder={t`Search Foreman`}
                showSearchBar={true}
                autoPosition={false}
                onSearchInputChange={(text) => {
                  if (text.length > 0 && text.length < 3) return;
                  setSearchForemanText(text);
                }}
                term={searchForemanText}
                setTerm={setSearchForemanText}
                items={filteredForemanListData}
                itemComponent={PersonaItem}
                selectedComponent={SelectedForeman}
                size={TextFieldSize.SMALL}
                showSelectAll={true}
                confirmSelection={true}
                appearance={TextFieldAppearance.NORMAL}
                displayKeyField="name"
                placeholder={t`Foreman`}
                onSelectedItemsChange={(selectedItems) => {
                  setSelectedForeman(selectedItems);
                  setIsSelectionChanged(true);
                }}
              />
            )}
          </Box>
        )}
        <Box
          data-testid="searchbar-filter-container"
          ml={{ sm: '400' }}
          css={`
            max-width: ${({ theme }) => theme.spacings[800]};
          `}
        >
          <SearchFilterButtonStyled
            data-testid="button-side-filter-drawer"
            appearance={ButtonAppearance.SECONDARY}
            isDefaultChange={!isEqual(drawerValues, defaultValues)}
            size={ButtonSize.SMALL}
            onClick={toggle}
          >
            <StyledIcon
              icon="sliders-v"
              size={IconSize.SMALL}
              fillColor={!isEqual(drawerValues, defaultValues) ? 'white' : ''}
            />
          </SearchFilterButtonStyled>
        </Box>
        {features?.showMapView && enableProjectMap && (
          <Box ml={{ sm: '400' }} data-testid="searchbar-mapMarker-container">
            <MapMarkerButtonStyled
              data-testid="button-map-marker"
              appearance={ButtonAppearance.SECONDARY}
              size={ButtonSize.SMALL}
              isActive={showMap}
              onClick={() => toggleMap(!showMap)}
            >
              <Icon icon="map-marker-alt" size={IconSize.SMALL} fillColor={showMap ? 'white' : 'bechtel.blue.700'} />
            </MapMarkerButtonStyled>
          </Box>
        )}
      </Box>
      <ErrorBoundary>
        <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
          {visible && (
            <DrawerContainerStyled>
              <WeldSearchDrawer
                close={close}
                initialValues={drawerValues}
                onSubmit={onDrawerSubmit}
                drawerForDashboard={true}
                personaType={loggedUserPersona}
              />
            </DrawerContainerStyled>
          )}
        </Suspense>
      </ErrorBoundary>
    </Fragment>
  );
};
